/**
 * Data transformation utilities for converting between Fitter and Analytics formats
 */

/**
 * Convert Fitter shot data to Analytics shot group format
 *
 * Fitter format: Array of shots with flat structure
 * Analytics format: Array of shot groups with nested structure
 *
 * @param {Array} fitterShots - Array of shot objects from Fitter API
 * @param {Object} options - Optional configuration
 * @param {string} options.groupName - Name for the shot group (default: "Current Session")
 * @param {string} options.groupColor - Color for the shot group (default: "#3b82f6")
 * @param {boolean} options.groupIsBaseline - Whether this is a baseline group (default: true)
 * @returns {Array} Array of shot groups in Analytics format
 */
export function fitterToAnalytics(fitterShots, options = {}) {
  if (!fitterShots || fitterShots.length === 0) {
    return [];
  }

  const {
    groupName = "Current Session",
    groupColor = "#3b82f6",
    groupIsBaseline = true
  } = options;

  // Transform each shot to Analytics format
  const transformedShots = fitterShots.map((shot, index) => ({
    shotNumber: shot.shot_number || index + 1,
    included: true, // All shots included by default
    metrics: {
      // Core metrics
      clubSpeed: shot.club_speed || 0,
      ballSpeed: shot.ball_speed || 0,
      smashFactor: shot.smash_factor || (shot.ball_speed && shot.club_speed ? shot.ball_speed / shot.club_speed : 0),
      launchAngle: shot.launch_angle || 0,
      spinRate: shot.spin_rate || 0,
      carry: shot.carry_distance || 0,
      total: shot.total_distance || shot.carry_distance || 0,

      // Ball flight
      height: shot.apex_height || shot.height || 0,
      landingAngle: shot.landing_angle || 0,
      attackAngle: shot.attack_angle || 0,
      offlineDistance: shot.offline_distance || 0,

      // Spin (alternatives)
      spinAxis: shot.spin_axis || null,
      sideSpin: shot.side_spin || null,

      // Club delivery
      clubPath: shot.club_path || null,
      faceAngle: shot.face_angle || null,
      dynamicLoft: shot.dynamic_loft || null,
    }
  }));

  // Return as a single shot group
  return [{
    id: "current-session",
    name: groupName,
    color: groupColor,
    isBaseline: groupIsBaseline,
    shots: transformedShots
  }];
}

/**
 * Convert Analytics shot group data to Fitter shot format
 *
 * @param {Array} shotGroups - Array of shot groups from Analytics
 * @param {Object} options - Optional configuration
 * @param {string} options.groupId - Specific group ID to convert (default: first group)
 * @returns {Array} Array of shots in Fitter format
 */
export function analyticsToFitter(shotGroups, options = {}) {
  if (!shotGroups || shotGroups.length === 0) {
    return [];
  }

  const { groupId } = options;

  // Select the group to convert
  const group = groupId
    ? shotGroups.find(g => g.id === groupId)
    : shotGroups[0];

  if (!group || !group.shots) {
    return [];
  }

  // Transform each shot to Fitter format
  return group.shots
    .filter(shot => shot.included) // Only include shots marked as included
    .map(shot => ({
      shot_number: shot.shotNumber,
      club_speed: shot.metrics.clubSpeed,
      ball_speed: shot.metrics.ballSpeed,
      smash_factor: shot.metrics.smashFactor,
      launch_angle: shot.metrics.launchAngle,
      spin_rate: shot.metrics.spinRate,
      carry_distance: shot.metrics.carry,
      total_distance: shot.metrics.total,
      apex_height: shot.metrics.height,
      landing_angle: shot.metrics.landingAngle,
      attack_angle: shot.metrics.attackAngle,
      offline_distance: shot.metrics.offlineDistance,
      spin_axis: shot.metrics.spinAxis,
      side_spin: shot.metrics.sideSpin,
      club_path: shot.metrics.clubPath,
      face_angle: shot.metrics.faceAngle,
      dynamic_loft: shot.metrics.dynamicLoft,
    }));
}

/**
 * Calculate aggregate statistics from shot data (Fitter or Analytics format)
 *
 * @param {Array} shots - Array of shots (either format)
 * @param {string} format - 'fitter' or 'analytics' (default: auto-detect)
 * @returns {Object} Aggregate statistics
 */
export function calculateAggregateStats(shots, format = 'auto') {
  if (!shots || shots.length === 0) {
    return null;
  }

  // Auto-detect format
  if (format === 'auto') {
    format = shots[0].metrics ? 'analytics' : 'fitter';
  }

  // Extract metrics based on format
  const getMetric = (shot, key) => {
    if (format === 'analytics') {
      const metricMap = {
        club_speed: 'clubSpeed',
        ball_speed: 'ballSpeed',
        launch_angle: 'launchAngle',
        spin_rate: 'spinRate',
        carry_distance: 'carry',
        total_distance: 'total',
      };
      return shot.metrics[metricMap[key]] || 0;
    }
    return shot[key] || 0;
  };

  const includedShots = format === 'analytics'
    ? shots.filter(s => s.included)
    : shots;

  if (includedShots.length === 0) {
    return null;
  }

  const count = includedShots.length;

  return {
    club_speed: includedShots.reduce((sum, s) => sum + getMetric(s, 'club_speed'), 0) / count,
    ball_speed: includedShots.reduce((sum, s) => sum + getMetric(s, 'ball_speed'), 0) / count,
    launch_angle: includedShots.reduce((sum, s) => sum + getMetric(s, 'launch_angle'), 0) / count,
    spin_rate: includedShots.reduce((sum, s) => sum + getMetric(s, 'spin_rate'), 0) / count,
    carry_distance: includedShots.reduce((sum, s) => sum + getMetric(s, 'carry_distance'), 0) / count,
    total_distance: includedShots.reduce((sum, s) => sum + getMetric(s, 'total_distance'), 0) / count,
    shot_count: count,
  };
}
